import { createSelector } from '@reduxjs/toolkit';
import { selectAllUsersPermissions } from '../slices/permissionsSlice';

import i18n from 'i18next';
import { sortBy, uniqBy } from 'lodash-es';
import { findResourcePermissionsFromParentProjects } from 'scenes/Permissions/utils/findPermissionsFromParentProjects.util';
import { returnEffectiveUserBusinessPermissions } from 'scenes/Permissions/utils/returnEffectiveUserBusinessPermissions.util';
import { returnEffectiveUserProjectPermissions } from 'scenes/Permissions/utils/returnEffectiveUserProjectPermissions.util';
import {
  selectProjects,
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import { selectAllResources } from 'store/resources/resourceSliceSelectors';
import { selectLoggedInUserId } from 'store/user/selectors.js/user.selectors';

const selectPermissionsState = (state) => state.permissions;

export const selectAdminEntities = createSelector(
  [selectPermissionsState],
  (state) => state.admins.entities
);

const selectRolesEntities = createSelector(
  [selectPermissionsState],
  (state) => state.roles.entities
);

export const selectIsLoadingChangeHistory = createSelector(
  [selectPermissionsState],
  (state) => state.isLoadingChangeHistory
);

export const selectChangeHistoryItemsEntities = createSelector(
  [selectPermissionsState],
  (state) => state.changeHistoryItems.entities
);
export const selectChangeHistoryItemsArray = createSelector(
  [selectChangeHistoryItemsEntities],
  (entities) => Object.values(entities)
);

export const selectChangeHistoryItemsArrayWithJoinedData = createSelector(
  [selectChangeHistoryItemsArray, selectAllResources],
  (historyItems, resources) =>
    historyItems.map((historyItem) => {
      const historyItemUserResource = resources.find(
        (resource) => resource.user === historyItem.userId
      );

      if (historyItem.userId === 'inherit_from_original_role') {
        return {
          ...historyItem,
          userName: 'Muutos yläprojektista kopioidussa roolissa',
        };
      } else {
        return {
          ...historyItem,
          userName: `${historyItemUserResource?.firstName} ${historyItemUserResource.lastName}`,
        };
      }
    })
);

export const selectRolesArray = createSelector([selectRolesEntities], (roles) =>
  Object.values(roles)
);

export const selectAssignedDefaultRolesEntities = createSelector(
  [selectPermissionsState],
  (state) => state.assignedDefaultRoles.entities
);

export const selectAssignedDefaultRolesArray = createSelector(
  [selectAssignedDefaultRolesEntities],
  (defaultRoles) => Object.values(defaultRoles)
);

export const selectSelectedProjectAssignedDefaultRoles = createSelector(
  [selectAssignedDefaultRolesArray, selectSelectedProjectId],
  (defaultRoles, projectId) => {
    return defaultRoles.filter(
      (defaultRole) => defaultRole.projectId === projectId
    );
  }
);

export const selectSelectedUsersToUpdatePermissions = createSelector(
  [selectPermissionsState],
  (state) => state.selectedUsersToUpdatePermissions
);

export const selectUserSearchFilter = createSelector(
  [selectPermissionsState],
  (state) => state.userSearchFilter
);

export const selectProjectRolePermissionModifications = createSelector(
  [selectPermissionsState],
  (state) => state.projectRolePermissionModifications
);

export const selectAssignedDefaultRolesModifications = createSelector(
  [selectPermissionsState],
  (state) => state.assignedDefaultRoleModifications
);

export const selectUserProjectPermissionModifications = createSelector(
  [selectPermissionsState],
  (state) => state.userPermissionModifications
);

export const selectUserBusinessPermissionModifications = createSelector(
  [selectPermissionsState],
  (state) => state.userBusinessPermissionModifications
);

export const selectUserRoleModifications = createSelector(
  [selectPermissionsState],
  (state) => state.userRoleModifications
);

export const selectIsDeletingProjectRole = createSelector(
  [selectPermissionsState],
  (state) => state.isDeletingProjectRole
);

export const selectIsCreatingNewRole = createSelector(
  [selectPermissionsState],
  (state) => state.isCreatingNewRole
);

export const selectIsUpdatingPermissionsOfUser = createSelector(
  [selectPermissionsState],
  (state) => state.isUpdatingPermissionsOfUser
);

export const selectIsUpdatingProjectRoles = createSelector(
  [selectPermissionsState],
  (state) => state.isUpdatingProjectRoles
);

export const selectIsConfirmUpdateRolesDialogOpen = createSelector(
  [selectPermissionsState],
  (state) => state.isConfirmUpdateRolesDialogOpen
);

export const selectIsUpdatingUserRoles = createSelector(
  [selectPermissionsState],
  (state) => state.isUpdatingUserRoles
);

export const selectUsersGroupedBy = createSelector(
  [selectPermissionsState],
  (state) => state.usersGroupedBy
);

export const selectSelectedUsersToAssignRole = createSelector(
  [selectPermissionsState],
  (state) => state.selectedUsersToAssignRole
);

export const selectUserBusinessPermissionsEntities = createSelector(
  [selectPermissionsState],
  (state) => state.userBusinessPermissions.entities
);

export const selectUserBusinessPermissionsArray = createSelector(
  [selectUserBusinessPermissionsEntities],
  (usersBusinessPermissionsEntities) =>
    Object.values(usersBusinessPermissionsEntities)
);

export const selectProjectRoles = createSelector(
  [selectRolesArray, (_, args) => args],
  (allRoles, args) => {
    const { projectId } = args;

    const projectRoles = allRoles.filter(
      (role) => role.projectId === projectId
    );

    return Object.values(projectRoles);
  }
);

const selectProjectRolesArrayWithoutDeleted = createSelector(
  [selectRolesArray],
  (roles) => roles.filter((role) => !role.deletedAt)
);

export const selectSelectedProjectRoles = createSelector(
  [selectProjectRolesArrayWithoutDeleted, selectSelectedProjectId],
  (roles, selectedProjectId) =>
    roles.filter((role) => role.projectId === selectedProjectId)
);

export const selectSelectedProjectEmployeeDefaultRole = createSelector(
  [
    selectSelectedProjectAssignedDefaultRoles,
    selectSelectedProjectRoles,
    selectAssignedDefaultRolesModifications,
  ],
  (projectDefaultRoles, projectRoles, assignedDefaultRolesModifications) => {
    const assignedDefaultEmployeeRole = projectDefaultRoles.find(
      (role) => role.SK === 'DEFAULT_ROLE_EMPLOYEE'
    );

    const assignedEmployeeDefaultRoleModification = Object.values(
      assignedDefaultRolesModifications
    ).find(
      (defaultRoleModification) =>
        defaultRoleModification.SK === 'DEFAULT_ROLE_EMPLOYEE'
    );

    if (assignedEmployeeDefaultRoleModification) {
      const assignedRole = projectRoles.find(
        (role) => role.roleId === assignedEmployeeDefaultRoleModification.roleId
      );
      return assignedRole;
    } else if (assignedDefaultEmployeeRole) {
      const currentAssignedDefaultEmployeeRoleId = assignedDefaultEmployeeRole
        ? assignedDefaultEmployeeRole.roleId
        : undefined;

      const assignedRole = projectRoles.find(
        (role) => role.roleId === currentAssignedDefaultEmployeeRoleId
      );

      return assignedRole;
    } else {
      return undefined;
    }
  }
);

export const selectSelectedProjectExternalDefaultRole = createSelector(
  [
    selectSelectedProjectAssignedDefaultRoles,
    selectSelectedProjectRoles,
    selectAssignedDefaultRolesModifications,
  ],
  (projectDefaultRoles, projectRoles, assignedDefaultRolesModifications) => {
    const assignedDefaultExternalRole = projectDefaultRoles.find(
      (role) => role.SK === 'DEFAULT_ROLE_EXTERNAL'
    );

    const assignedExternalDefaultRoleModification = Object.values(
      assignedDefaultRolesModifications
    ).find(
      (defaultRoleModification) =>
        defaultRoleModification.SK === 'DEFAULT_ROLE_EXTERNAL'
    );

    if (assignedExternalDefaultRoleModification) {
      const assignedRole = projectRoles.find(
        (role) => role.roleId === assignedExternalDefaultRoleModification.roleId
      );
      return assignedRole;
    } else if (assignedDefaultExternalRole) {
      const currentAssignedDefaultExternalRoleId = assignedDefaultExternalRole
        ? assignedDefaultExternalRole.roleId
        : undefined;

      const assignedRole = projectRoles.find(
        (role) => role.roleId === currentAssignedDefaultExternalRoleId
      );

      return assignedRole;
    } else {
      return undefined;
    }
  }
);

export const selectSelectedProjectExternalEmployeeDefaultRole = createSelector(
  [
    selectSelectedProjectAssignedDefaultRoles,
    selectSelectedProjectRoles,
    selectAssignedDefaultRolesModifications,
  ],
  (projectDefaultRoles, projectRoles, assignedDefaultRolesModifications) => {
    const assignedDefaultExternalRole = projectDefaultRoles.find(
      (role) => role.SK === 'DEFAULT_ROLE_EMPLOYEE_EXTERNAL'
    );

    const assignedExternalEmployeeDefaultRoleModification = Object.values(
      assignedDefaultRolesModifications
    ).find(
      (defaultRoleModification) =>
        defaultRoleModification.SK === 'DEFAULT_ROLE_EMPLOYEE_EXTERNAL'
    );

    if (assignedExternalEmployeeDefaultRoleModification) {
      const assignedRole = projectRoles.find(
        (role) =>
          role.roleId === assignedExternalEmployeeDefaultRoleModification.roleId
      );
      return assignedRole;
    } else if (assignedDefaultExternalRole) {
      const currentAssignedDefaultExternalRoleId = assignedDefaultExternalRole
        ? assignedDefaultExternalRole.roleId
        : undefined;

      const assignedRole = projectRoles.find(
        (role) => role.roleId === currentAssignedDefaultExternalRoleId
      );

      return assignedRole;
    } else {
      return undefined;
    }
  }
);

export const selectSelectedProjectRolesWithModifications = createSelector(
  [selectSelectedProjectRoles, selectProjectRolePermissionModifications],
  (roles, roleModifications) =>
    roles.map((role) => {
      const currentRoleModifications = roleModifications[role.roleId];

      if (currentRoleModifications) {
        return {
          ...role,
          permissions: { ...role.permissions, ...currentRoleModifications },
          ...currentRoleModifications,
        };
      } else {
        return role;
      }
    })
);

export const selectUserPermissionsWithModifications = createSelector(
  [selectAllUsersPermissions, selectUserRoleModifications],
  (userPermissions, userRoleModifications) =>
    userPermissions.map((singleUserProjectPermissions) => {
      const { userId, projectId } = singleUserProjectPermissions;
      const currentUserRoleModifications =
        userRoleModifications[`${userId}#${projectId}`];

      if (currentUserRoleModifications) {
        return {
          ...singleUserProjectPermissions,
          roleId: currentUserRoleModifications.roleId,
        };
      } else {
        return singleUserProjectPermissions;
      }
    })
);

export const selectPermissionsOfProjectResources = createSelector(
  [
    selectAdminEntities,
    selectRolesArray,
    selectAllResources,
    selectUserPermissionsWithModifications,
    selectUserBusinessPermissionsArray,
    selectSelectedProjectId,
    selectSelectedProject,
    selectProjects,
    selectUserRoleModifications,
  ],
  (
    adminEntities,
    allRoles,
    allResources,
    usersProjectPermissions,
    usersBusinessPermissions,
    selectedProjectId,
    selectedProject,
    projectsMap,
    userRoleModifications
  ) => {
    const sortedResources = sortBy(allResources, ['firstName']);

    let projectResources = [];

    if (selectedProject.businessTypeEnum === 'EMPLOYER') {
      const employerBusinessProjectResources = sortedResources.filter(
        (resource) => resource.projectBusinessId === selectedProject.business_id
      );

      projectResources = employerBusinessProjectResources;
    } else {
      projectResources = sortedResources.filter(
        (resource) => resource.project === selectedProjectId
      );
    }

    const uniqueProjectResources = uniqBy(projectResources, 'user');

    const resourcesWithPermissions = uniqueProjectResources.map((resource) => {
      let foundUserProjectPermissions = usersProjectPermissions.find(
        (userProjectPermissions) =>
          userProjectPermissions.userId === resource.user &&
          userProjectPermissions.projectId === selectedProjectId
      );

      if (!foundUserProjectPermissions) {
        if (userRoleModifications[`${resource.user}#${selectedProjectId}`]) {
          foundUserProjectPermissions =
            userRoleModifications[`${resource.user}#${selectedProjectId}`];
        } else {
          foundUserProjectPermissions =
            findResourcePermissionsFromParentProjects(
              selectedProject.parent,
              projectsMap,
              usersProjectPermissions,
              resource
            );
        }
      }

      let effectiveBusinessPermissions;

      if (selectedProject.businessTypeEnum === 'EMPLOYER') {
        effectiveBusinessPermissions = returnEffectiveUserBusinessPermissions(
          usersBusinessPermissions,
          allRoles,
          resource,
          selectedProject
        );
      }

      const {
        roleName,
        userRoleId,
        rolePermissions,
        rolePermissionsWithUserPermissions,
      } = returnEffectiveUserProjectPermissions(
        foundUserProjectPermissions,
        allRoles,
        resource
      );

      const employerData = projectsMap[resource.employer];

      resource = {
        ...resource,
        employerData: employerData ? { name: employerData.name } : undefined,
        permissions: {
          roleName,
          userRoleId,
          rolePermissions,
          businessPermissions: effectiveBusinessPermissions,
          userPermissions: rolePermissionsWithUserPermissions,
          inheritedProject: foundUserProjectPermissions.inheritedProject,
          isAdmin: adminEntities[
            `${resource.user}#${selectedProject.business_id}`
          ]
            ? true
            : false,
        },
      };

      return resource;
    });

    return resourcesWithPermissions;
  }
);

const selectSearchFilteredPermissionsResources = createSelector(
  [selectPermissionsOfProjectResources, selectUserSearchFilter],
  (permissionsResources, searchText) => {
    if (!searchText) {
      return permissionsResources;
    }

    const searchTerms = searchText
      .toLowerCase()
      .split(' ')
      .filter((term) => term.length > 0);

    return permissionsResources.filter((resource) => {
      const searchableValue = `${resource.firstName} ${resource.lastName} ${
        resource.employerData?.name || ''
      } ${resource.employerBusinessId || ''}`.toLowerCase();

      return searchTerms.every((term) => searchableValue.includes(term));
    });
  }
);

const returnEmployerGroupedResources = (resources) => {
  const groups = new Map();

  resources.forEach((resource) => {
    let key, name, sortingName;

    if (!resource.employerBusinessId) {
      if (resource.employmentType === 'RESIDENT') {
        key = 'RESIDENTS';
        name = i18n.t('calendar.residents');
        sortingName = 'zzzz';
      } else {
        key = 'MyLixani';
        name = 'MyLixani';
        sortingName = 'yyyy';
      }
    } else {
      key = resource.employerBusinessId;
      name = resource.employerData?.name || key;
      sortingName = name.toLowerCase();
    }

    if (!groups.has(key)) {
      groups.set(key, {
        name,
        sortingName,
        business_id: resource.employerBusinessId,
        employeeResources: [],
        groupIndex: key,
      });
    }

    groups.get(key).employeeResources.push(resource);
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.sortingName.localeCompare(b.sortingName)
  );
};

export const selectResourcesGroupedByEmployer = createSelector(
  [selectSearchFilteredPermissionsResources],
  (resources) => {
    return returnEmployerGroupedResources(resources);
  }
);

export const selectResourcesGroupedByRole = createSelector(
  [selectSearchFilteredPermissionsResources],
  (resources) => {
    const roleGroups = new Map();

    resources.forEach((resource) => {
      const roleKey = resource.permissions.isAdmin
        ? 'ADMIN'
        : resource.permissions.roleName || 'undefined';

      if (!roleGroups.has(roleKey)) {
        roleGroups.set(roleKey, []);
      }
      roleGroups.get(roleKey).push(resource);
    });

    const result = Array.from(roleGroups.entries()).map(
      ([key, roleResources]) => {
        const name = key === 'undefined' ? i18n.t('permissions.no_role') : key;
        const sortingName = key === 'undefined' ? 'zzzz' : key.toLowerCase();

        return {
          name,
          businessResources: returnEmployerGroupedResources(roleResources),
          sortingName,
          groupIndex: key,
        };
      }
    );

    return result.sort((a, b) => a.sortingName.localeCompare(b.sortingName));
  }
);

export const selectPermissionsViewGroupedResources = createSelector(
  [
    selectResourcesGroupedByEmployer,
    selectResourcesGroupedByRole,
    selectUsersGroupedBy,
  ],
  (resourcesGroupedByEmployer, resourcesGroupedByRole, usersGroupedBy) =>
    usersGroupedBy === 'business'
      ? resourcesGroupedByEmployer
      : resourcesGroupedByRole
);

export const selectIsPermissionsLoading = createSelector(
  [selectPermissionsState],
  (state) => state.loading
);
export const selectIsAdminsLoaded = createSelector(
  [selectPermissionsState],
  (state) => state.adminsLoaded
);

export const selectIsAdminOfBusiness = createSelector(
  [selectAdminEntities, selectLoggedInUserId, selectSelectedProject],
  (adminEntities, loggedInUserId, selectedProject) =>
    adminEntities[`${loggedInUserId}#${selectedProject.business_id}`]
      ? true
      : false
);

export const selectUserRoleModificationChanges = createSelector(
  [selectUserRoleModifications, selectAllResources, selectSelectedProjectRoles],
  (roleModifications, resources, projectRoles) => {
    const changes = [];

    Object.values(roleModifications).forEach((roleModification) => {
      const userResource = resources.find(
        (resource) => resource.user === roleModification.userId
      );

      const assignedRole = projectRoles.find(
        (role) => role.roleId === roleModification.roleId
      );

      changes.push({
        userName: `${userResource.firstName} ${userResource.lastName}`,
        roleName: assignedRole?.roleName || i18n.t('permissions.no_role'),
      });
    });

    return changes;
  }
);

export const selectSelectedUsersDataToAssignRoleTo = createSelector(
  [selectSelectedUsersToAssignRole, selectAllResources],
  (selectedUserIds, resources) =>
    selectedUserIds.map((userId) => {
      const selectedUserResource = resources.find(
        (resource) => resource.user === userId
      );

      return {
        userName: `${selectedUserResource?.firstName} ${selectedUserResource.lastName}`,
      };
    })
);
